// Softphone Context using JsSIP (works better with Asterisk)
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import JsSIP from 'jssip';
import { syncAgentPresence, unregisterAgentPresence, sendAgentHeartbeat } from '../lib/agentPresence';
import { SIP_DOMAIN, SIP_REGISTRAR, SIP_WS_URL, getIceServers } from '../config/sip';

const SoftphoneContext = createContext(null);

const LOCAL_RINGBACK_ASSET_PATH = 'RingTone/standardringtone.mp3';
const CALL_AUDIO_OUTPUT_DEVICE_ID_KEY = 'frontOfficeNew_callAudioOutputDeviceId';
const CALL_AUDIO_OUTPUT_CHANGED_EVENT = 'frontOfficeNew:call-audio-output-changed';
const HEADSET_LABEL_PATTERN = /\b(headsets?|headphones?|earphones?|earbuds?|airpods?)\b/i;
const LOCAL_RINGBACK_FALLBACK_DELAY_MS = Math.max(
  0,
  Number.parseInt(String(process.env.REACT_APP_OUTBOUND_RINGBACK_FALLBACK_DELAY_MS || '1800'), 10) || 1800
);
// How long the softphone stays ready to auto-answer the next incoming SIP leg.
// For OUTBOUND calls the agent leg only rings after the customer answers (the
// server dials the customer with a 30s timeout), so this must comfortably exceed
// that customer ring time — otherwise the window expires before the agent's
// INVITE arrives and the call collapses ("Canceled").
const AUTO_ANSWER_WINDOW_MS = Math.max(
  15000,
  Number.parseInt(String(process.env.REACT_APP_AUTO_ANSWER_WINDOW_MS || '60000'), 10) || 60000
);
const SIP_ANSWER_TIMEOUT_MS = Math.max(
  5000,
  Number.parseInt(String(process.env.REACT_APP_SIP_ANSWER_TIMEOUT_MS || '15000'), 10) || 15000
);
// JsSIP waits for ICE gathering to finish before it sends the SIP 200 OK. A
// dead/unreachable STUN or TURN route can otherwise leave an incoming call in
// 180 Ringing until the caller gives up. Give ICE a short window to collect a
// relay candidate, then continue with every candidate gathered so far.
const ICE_GATHERING_GRACE_MS = Math.max(
  1000,
  Number.parseInt(String(process.env.REACT_APP_ICE_GATHERING_GRACE_MS || '2500'), 10) || 2500
);
const AUDIO_MEDIA_CONSTRAINTS = {
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true
  },
  video: false
};

const getLocalRingbackUrl = () => {
  const publicUrl = String(process.env.PUBLIC_URL || '').replace(/\/+$/, '');
  return publicUrl ? `${publicUrl}/${LOCAL_RINGBACK_ASSET_PATH}` : `/${LOCAL_RINGBACK_ASSET_PATH}`;
};

// RTCPeerConnection config applied to every call (outbound + inbound). Feeds the
// STUN/TURN servers into WebRTC so media can traverse NAT/firewalls.
const buildPcConfig = () => ({
  iceServers: getIceServers(),
  rtcpMuxPolicy: 'require'
});

const describeIceServers = (servers) => {
  if (!servers || !servers.length) {
    return '🔴 ICE servers: NONE — STUN/TURN configured nahi (media dusre network par fail hoga)';
  }
  const parts = servers.map((server) => {
    const url = Array.isArray(server.urls) ? server.urls[0] : server.urls;
    return `${url}${server.username ? ' (TURN)' : ' (STUN)'}`;
  });
  return `🧊 ICE servers active: ${parts.join(', ')}`;
};

export const waitForSipAnswerAccepted = (
  session,
  answerOptions,
  onIceGatheringRelease
) => new Promise((resolve, reject) => {
  let settled = false;
  let timeoutId = null;
  let iceGatheringTimerId = null;
  let iceGatheringDeadlineReached = false;
  let latestIceReady = null;
  const iceCandidateTally = { host: 0, srflx: 0, prflx: 0, relay: 0, unknown: 0 };

  const removeListener = (eventName, listener) => {
    if (typeof session.removeListener === 'function') {
      session.removeListener(eventName, listener);
    } else if (typeof session.off === 'function') {
      session.off(eventName, listener);
    }
  };

  const cleanup = () => {
    if (timeoutId) {
      window.clearTimeout(timeoutId);
    }
    if (iceGatheringTimerId) {
      window.clearTimeout(iceGatheringTimerId);
    }
    removeListener('accepted', handleAccepted);
    removeListener('failed', handleFailed);
    removeListener('ended', handleEnded);
    removeListener('icecandidate', handleIceCandidate);
  };

  const finish = (callback, value) => {
    if (settled) return;
    settled = true;
    cleanup();
    callback(value);
  };

  const describeFailure = (event, fallback) => (
    event?.cause || event?.message || event?.response?.reason_phrase || fallback
  );

  const handleAccepted = () => finish(resolve, true);
  const handleFailed = (event) => finish(
    reject,
    new Error(describeFailure(event, 'SIP answer failed'))
  );
  const handleEnded = (event) => finish(
    reject,
    new Error(describeFailure(event, 'Call ended before SIP answer was accepted'))
  );

  const releaseIceGathering = (reason) => {
    if (typeof latestIceReady !== 'function') return false;

    const ready = latestIceReady;
    latestIceReady = null;
    removeListener('icecandidate', handleIceCandidate);
    if (iceGatheringTimerId) {
      window.clearTimeout(iceGatheringTimerId);
      iceGatheringTimerId = null;
    }

    try {
      ready();
      if (typeof onIceGatheringRelease === 'function') {
        try {
          onIceGatheringRelease(reason, { ...iceCandidateTally });
        } catch {
          // Diagnostics must never turn a released SIP answer into a failure.
        }
      }
      return true;
    } catch (error) {
      finish(reject, error);
      return false;
    }
  };

  const handleIceCandidate = ({ candidate, ready } = {}) => {
    if (typeof ready !== 'function') return;

    latestIceReady = ready;
    const match = candidate?.candidate?.match(/ typ (\w+)/);
    const type = match?.[1] || 'unknown';
    iceCandidateTally[type] = (iceCandidateTally[type] || 0) + 1;

    // A relay candidate is the most robust NAT path, so there is no reason to
    // keep the SIP answer waiting once one has been gathered.
    if (type === 'relay') {
      releaseIceGathering('relay candidate available');
    } else if (iceGatheringDeadlineReached) {
      releaseIceGathering('ICE gathering deadline reached');
    }
  };

  session.on('accepted', handleAccepted);
  session.on('failed', handleFailed);
  session.on('ended', handleEnded);
  session.on('icecandidate', handleIceCandidate);

  iceGatheringTimerId = window.setTimeout(() => {
    iceGatheringDeadlineReached = true;
    releaseIceGathering('ICE gathering deadline reached');
  }, ICE_GATHERING_GRACE_MS);

  timeoutId = window.setTimeout(() => {
    finish(reject, new Error('Timed out waiting for SIP 200 OK'));
  }, SIP_ANSWER_TIMEOUT_MS);

  try {
    session.answer(answerOptions);
  } catch (error) {
    finish(reject, error);
  }
});

export const useSoftphone = () => {
  const context = useContext(SoftphoneContext);
  if (!context) {
    throw new Error('useSoftphone must be used within SoftphoneProvider');
  }
  return context;
};

export const SoftphoneProvider = ({ children }) => {
  const [status, setStatus] = useState('Disconnected');
  const [registered, setRegistered] = useState(false);
  const [incomingCall, setIncomingCall] = useState(null);
  const [currentCall, setCurrentCall] = useState(null);
  const [muted, setMuted] = useState(false);
  const [logs, setLogs] = useState([]);

  const uaRef = useRef(null);
  const sessionRef = useRef(null);
  const audioRef = useRef(null);
  const localAudioStreamRef = useRef(null);
  const remoteStreamIdRef = useRef('');
  const peerStatsTimerRef = useRef(null);
  const connectRef = useRef(null);
  const autoConnectAttempted = useRef(false);
  const keepaliveIntervalRef = useRef(null);
  const reregisterTimerRef = useRef(null);
  const autoAnswerRequestedRef = useRef({ expiresAt: 0 });
  const answerInFlightRef = useRef({ session: null, promise: null });
  const registeredRef = useRef(registered);
  const pageTeardownRef = useRef(false);

  // Enable JsSIP debug in development
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      JsSIP.debug.enable('JsSIP:*');
    }
  }, []);

  useEffect(() => {
    registeredRef.current = registered;
  }, [registered]);

  const getExtension = useCallback(() => {
    try {
      const stored = localStorage.getItem('extension');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.error('Failed to load extension:', e);
    }
    return null;
  }, []);

  const log = useCallback((msg) => {
    const timestamp = new Date().toLocaleTimeString();
    console.log(`[Softphone ${timestamp}] ${msg}`);
    setLogs((prev) => [...prev.slice(-50), `${timestamp} ${msg}`]);
  }, []);

  const stopPeerStatsMonitor = useCallback(() => {
    if (peerStatsTimerRef.current) {
      clearInterval(peerStatsTimerRef.current);
      peerStatsTimerRef.current = null;
    }
  }, []);

  const stopLocalAudioStream = useCallback(() => {
    const stream = localAudioStreamRef.current;
    localAudioStreamRef.current = null;
    if (!stream) return;
    stream.getTracks().forEach((track) => {
      try {
        track.stop();
      } catch {
        // Ignore cleanup failures.
      }
    });
  }, []);

  const ensureLocalAudioStream = useCallback(async () => {
    const existing = localAudioStreamRef.current;
    const activeAudioTracks = existing?.getAudioTracks?.().filter((track) => track.readyState === 'live') || [];
    if (activeAudioTracks.length) {
      activeAudioTracks.forEach((track) => {
        track.enabled = true;
      });
      return existing;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error('Browser microphone API unavailable');
    }

    const stream = await navigator.mediaDevices.getUserMedia(AUDIO_MEDIA_CONSTRAINTS);
    const tracks = stream.getAudioTracks();
    if (!tracks.length) {
      throw new Error('No microphone audio track available');
    }
    tracks.forEach((track) => {
      track.enabled = true;
    });
    localAudioStreamRef.current = stream;
    log(`Microphone ready: ${tracks.map((track) => track.label || track.id || 'audio').join(', ')}`);
    return stream;
  }, [log]);

  const startPeerStatsMonitor = useCallback((peerconnection, source = 'call') => {
    if (!peerconnection?.getStats) return;
    stopPeerStatsMonitor();

    const writeStats = async () => {
      try {
        const senders = typeof peerconnection.getSenders === 'function'
          ? peerconnection.getSenders().filter((sender) => sender.track?.kind === 'audio')
          : [];
        const micState = senders
          .map((sender) => `${sender.track?.enabled ? 'on' : 'off'}/${sender.track?.readyState || 'unknown'}`)
          .join(',') || 'missing';

        const report = await peerconnection.getStats();
        let outboundPackets = 0;
        let outboundBytes = 0;
        let inboundPackets = 0;
        let inboundBytes = 0;
        let selectedPair = null;
        const candidates = {};

        report.forEach((stat) => {
          if (
            stat.type === 'candidate-pair' &&
            (stat.nominated || stat.selected) &&
            stat.state === 'succeeded'
          ) {
            selectedPair = stat;
          } else if (stat.type === 'local-candidate' || stat.type === 'remote-candidate') {
            candidates[stat.id] = stat;
          }
          const isAudio = stat.kind === 'audio' || stat.mediaType === 'audio';
          if (!isAudio) return;
          if (stat.type === 'outbound-rtp' && !stat.isRemote) {
            outboundPackets += Number(stat.packetsSent || 0);
            outboundBytes += Number(stat.bytesSent || 0);
          }
          if (stat.type === 'inbound-rtp' && !stat.isRemote) {
            inboundPackets += Number(stat.packetsReceived || 0);
            inboundBytes += Number(stat.bytesReceived || 0);
          }
        });

        // Which ICE candidate pair carries the media: host⇄host (direct) vs relay (TURN).
        let pathInfo = 'path=none(NAT/ICE)';
        if (selectedPair) {
          const localType = candidates[selectedPair.localCandidateId]?.candidateType || '?';
          const remoteType = candidates[selectedPair.remoteCandidateId]?.candidateType || '?';
          pathInfo = `path=${localType}⇄${remoteType}`;
        }

        log(
          `WebRTC ${source}: ice=${peerconnection.iceConnectionState || 'n/a'}, ` +
          `mic=${micState}, out=${outboundPackets}/${outboundBytes}B, in=${inboundPackets}/${inboundBytes}B, ${pathInfo}`
        );

        const iceState = peerconnection.iceConnectionState;
        if (iceState === 'connected' || iceState === 'completed') {
          if (inboundPackets === 0) {
            log('🔴 Incoming audio 0 packets — aap ko doosri taraf ki awaz NAHI aa rahi (media IN path down)');
          }
          if (outboundPackets === 0) {
            log('🔴 Outgoing audio 0 packets — doosri taraf ko aap ki awaz NAHI ja rahi (mic/media OUT path down)');
          }
        }
      } catch (error) {
        log(`WebRTC stats failed: ${error?.message || error}`);
      }
    };

    writeStats();
    peerStatsTimerRef.current = window.setInterval(writeStats, 5000);
  }, [log, stopPeerStatsMonitor]);

  // Local outbound fallback ringback. It is armed on dial, then cancelled if SIP
  // progress/answer/audio arrives before the fallback delay expires.
  const ringbackRef = useRef({
    ctx: null,
    osc: null,
    gain: null,
    timer: null,
    fallbackTimer: null,
    stopTimer: null,
    audio: null
  });

  const stopLocalRingback = useCallback(() => {
    const rb = ringbackRef.current;
    if (rb.fallbackTimer) {
      clearTimeout(rb.fallbackTimer);
      rb.fallbackTimer = null;
    }
    if (rb.timer) {
      clearTimeout(rb.timer);
      rb.timer = null;
    }
    if (rb.stopTimer) {
      clearInterval(rb.stopTimer);
      rb.stopTimer = null;
    }
    if (rb.audio) {
      if (rb.audio.dataset) {
        rb.audio.dataset.ringbackStopped = 'true';
      }
      rb.audio.onended = null;
      rb.audio.pause();
      try {
        rb.audio.currentTime = 0;
      } catch {
        // Ignore reset failures while metadata is unavailable.
      }
    }
    try { if (rb.osc) { rb.osc.stop(); rb.osc.disconnect(); } } catch (e) { /* noop */ }
    try { if (rb.gain) { rb.gain.disconnect(); } } catch (e) { /* noop */ }
    rb.osc = null;
    rb.gain = null;
  }, []);

  const playLocalRingbackTone = useCallback(() => {
    const rb = ringbackRef.current;
    if (rb.audio && !rb.audio.paused && !rb.audio.ended) return;

    try {
      const ringToneSrc = getLocalRingbackUrl();
      let audio = rb.audio;
      if (!audio) {
        audio = new Audio(ringToneSrc);
        audio.preload = 'auto';
        rb.audio = audio;
      } else if (audio.dataset?.ringbackSrc !== ringToneSrc) {
        audio.pause();
        audio.src = ringToneSrc;
        audio.preload = 'auto';
      }
      if (audio.dataset) {
        audio.dataset.ringbackSrc = ringToneSrc;
        audio.dataset.ringbackStopped = 'false';
      }

      const replayRingback = () => {
        if (audio.dataset?.ringbackStopped === 'true') return;
        try {
          audio.currentTime = 0;
          const replayAttempt = audio.play();
          if (replayAttempt && typeof replayAttempt.catch === 'function') {
            replayAttempt.catch(() => {});
          }
        } catch {
          // Keep retrying while the call remains in fallback ringback state.
        }
      };

      audio.loop = true;
      audio.volume = 0.55;
      audio.onended = replayRingback;
      if (rb.stopTimer) {
        clearInterval(rb.stopTimer);
      }
      rb.stopTimer = window.setInterval(() => {
        if (audio.dataset?.ringbackStopped === 'true') return;
        if (audio.paused || audio.ended) {
          replayRingback();
        }
      }, 1000);

      if (audio.paused || audio.ended) {
        audio.currentTime = 0;
        const playAttempt = audio.play();
        if (playAttempt && typeof playAttempt.catch === 'function') {
          playAttempt.catch(() => {});
        }
      }
      log('Outbound fallback ringtone started');
      return;
    } catch {
      // Fall back to the generated tone below if MP3 playback cannot be initialized.
    }

    if (rb.timer || rb.osc) return; // already ringing
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      if (!rb.ctx) rb.ctx = new AC();
      const ctx = rb.ctx;
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});

      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 425;   // standard ringback tone frequency
      gain.gain.value = 0;         // start silent; cadence toggles it on/off
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      rb.osc = osc;
      rb.gain = gain;

      // "toont toont" double-ring cadence: 0.4s on, 0.2s off, 0.4s on, 2s gap.
      const cadence = [[0.2, 400], [0, 200], [0.2, 400], [0, 2000]];
      let step = 0;
      const tick = () => {
        const cur = ringbackRef.current;
        if (!cur.gain || !cur.ctx) return;
        const [level, durationMs] = cadence[step % cadence.length];
        const now = cur.ctx.currentTime;
        try {
          cur.gain.gain.cancelScheduledValues(now);
          cur.gain.gain.setValueAtTime(cur.gain.gain.value, now);
          cur.gain.gain.linearRampToValueAtTime(level, now + 0.01); // small ramp avoids clicks
        } catch (e) { /* noop */ }
        step += 1;
        cur.timer = setTimeout(tick, durationMs);
      };
      tick();
      log('Outbound fallback ringback started');
    } catch (e) {
      // Web Audio unavailable/blocked — fail silently, no ringback.
    }
  }, [log]);

  const startLocalRingback = useCallback(() => {
    if (typeof window === 'undefined') return;

    const rb = ringbackRef.current;
    if (rb.fallbackTimer || rb.timer || rb.osc) return;

    rb.fallbackTimer = window.setTimeout(() => {
      rb.fallbackTimer = null;
      playLocalRingbackTone();
    }, LOCAL_RINGBACK_FALLBACK_DELAY_MS);
  }, [playLocalRingbackTone]);

  const applyCallAudioOutput = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio || typeof audio.setSinkId !== 'function') return false;

    try {
      const storedDeviceId = localStorage.getItem(CALL_AUDIO_OUTPUT_DEVICE_ID_KEY) || '';
      let deviceId = storedDeviceId;
      if (typeof navigator.mediaDevices?.enumerateDevices === 'function') {
        try {
          const outputs = (await navigator.mediaDevices.enumerateDevices())
            .filter((device) => device.kind === 'audiooutput');
          const storedDevice = outputs.find((device) => device.deviceId === storedDeviceId);
          const headset = outputs.find((device) => HEADSET_LABEL_PATTERN.test(device.label || ''));
          deviceId = storedDevice?.deviceId || headset?.deviceId || storedDeviceId;
        } catch {
          // The saved output can still be valid when device enumeration is unavailable.
        }
      }

      await audio.setSinkId(deviceId);
      return Boolean(deviceId);
    } catch (error) {
      log(`Call audio output fallback: ${error?.message || error}`);
      try {
        await audio.setSinkId('');
      } catch {
        // Browser default output remains active when explicit routing is unavailable.
      }
      return false;
    }
  }, [log]);

  useEffect(() => {
    const handleCallAudioOutputChanged = () => {
      applyCallAudioOutput().catch(() => {});
    };

    window.addEventListener(CALL_AUDIO_OUTPUT_CHANGED_EVENT, handleCallAudioOutputChanged);
    navigator.mediaDevices?.addEventListener?.('devicechange', handleCallAudioOutputChanged);
    return () => {
      window.removeEventListener(CALL_AUDIO_OUTPUT_CHANGED_EVENT, handleCallAudioOutputChanged);
      navigator.mediaDevices?.removeEventListener?.('devicechange', handleCallAudioOutputChanged);
    };
  }, [applyCallAudioOutput]);

  const attachRemoteAudio = useCallback((stream, source = 'remote') => {
    // Real SIP audio is arriving (early media or answered call) — stop our local tone.
    stopLocalRingback();
    const audio = audioRef.current;
    if (!audio || !stream) return;

    const streamId = stream.id || source;
    const alreadyAttached = audio.srcObject === stream || remoteStreamIdRef.current === streamId;

    audio.autoplay = true;
    audio.playsInline = true;
    if (audio.srcObject !== stream) {
      audio.srcObject = stream;
    }

    void applyCallAudioOutput().then(() => {
      const playAttempt = audio.play();
      if (playAttempt && typeof playAttempt.catch === 'function') {
        playAttempt.catch((error) => {
          log(`Audio autoplay blocked: ${error?.message || error}`);
        });
      }
    });

    if (!alreadyAttached) {
      remoteStreamIdRef.current = streamId;
      log(`Audio stream attached (${source})`);
    }
  }, [applyCallAudioOutput, log, stopLocalRingback]);

  const clearRemoteAudio = useCallback(() => {
    setMuted(false);
    stopLocalRingback();
    stopPeerStatsMonitor();
    stopLocalAudioStream();
    remoteStreamIdRef.current = '';
    const audio = audioRef.current;
    if (!audio) return;

    audio.pause();
    audio.srcObject = null;
  }, [stopLocalAudioStream, stopLocalRingback, stopPeerStatsMonitor]);

  const wireRemoteAudio = useCallback((peerconnection, source = 'call') => {
    if (!peerconnection) return;

    startPeerStatsMonitor(peerconnection, source);

    // ICE candidate gathering — reveals whether STUN/TURN produced reflexive/relay
    // candidates. Sirf host candidates = no NAT traversal on other networks.
    const candidateTally = { host: 0, srflx: 0, prflx: 0, relay: 0 };
    peerconnection.addEventListener('icecandidate', (event) => {
      if (!event.candidate || !event.candidate.candidate) {
        log(`🧊 ${source} ICE gathering complete — host:${candidateTally.host} srflx:${candidateTally.srflx} relay:${candidateTally.relay}`);
        if (candidateTally.srflx === 0 && candidateTally.relay === 0) {
          log('🔴 Sirf HOST (LAN) candidates mile — STUN/TURN configured NAHI. Isi liye doosre network par awaz nahi aati.');
        } else if (candidateTally.relay === 0) {
          log('⚠️ TURN (relay) candidate nahi mila — sakht firewall/symmetric-NAT wale network par media fail hoga.');
        }
        return;
      }
      const match = event.candidate.candidate.match(/ typ (\w+)/);
      const type = match ? match[1] : 'unknown';
      if (candidateTally[type] !== undefined) candidateTally[type] += 1;
    });
    peerconnection.addEventListener('icecandidateerror', (event) => {
      // STUN/TURN server tak na pohanchne par fire hota hai (galat URL/creds/port).
      const code = event.errorCode || 'unknown';
      const url = event.url || 'unknown ICE server';
      const reason = event.errorText || 'unknown error';
      log(`🔴 ${source} ICE candidate error: code=${code}, url=${url}, reason=${reason}`);
    });

    peerconnection.addEventListener('iceconnectionstatechange', () => {
      const state = peerconnection.iceConnectionState || 'unknown';
      log(`WebRTC ${source} ICE state: ${state}`);
      if (state === 'failed') {
        log('🔴 ICE FAILED — NAT traversal fail. TURN server chahiye. YEHI wajah hai jab awaz dono taraf nahi aati.');
      } else if (state === 'disconnected') {
        log('⚠️ ICE disconnected — media path beech me toot gaya (network drop/NAT timeout).');
      }
    });
    peerconnection.addEventListener('connectionstatechange', () => {
      log(`WebRTC ${source} connection state: ${peerconnection.connectionState || 'unknown'}`);
    });

    peerconnection.addEventListener('track', (event) => {
      const stream = event.streams?.[0] ||
        (event.track && typeof window !== 'undefined' && typeof window.MediaStream === 'function'
          ? new window.MediaStream([event.track])
          : null);
      attachRemoteAudio(stream, `${source}:track`);
    });

    peerconnection.addEventListener('addstream', (event) => {
      attachRemoteAudio(event.stream, `${source}:addstream`);
    });

    if (typeof peerconnection.getRemoteStreams === 'function') {
      peerconnection.getRemoteStreams().forEach((stream) => {
        attachRemoteAudio(stream, `${source}:remote-stream`);
      });
    }
  }, [attachRemoteAudio, log, startPeerStatsMonitor]);

  const clearAutoAnswer = useCallback((releasePreparedAudio = false) => {
    autoAnswerRequestedRef.current = { expiresAt: 0 };
    if (releasePreparedAudio && !sessionRef.current) {
      stopLocalAudioStream();
    }
  }, [stopLocalAudioStream]);

  const answerSession = useCallback((session, source = 'manual') => {
    if (!session) return Promise.resolve(false);

    const currentAttempt = answerInFlightRef.current;
    if (currentAttempt.session === session && currentAttempt.promise) {
      return currentAttempt.promise;
    }

    const attempt = (async () => {
      try {
        const mediaStream = await ensureLocalAudioStream();
        const pcConfig = buildPcConfig();
        log(describeIceServers(pcConfig.iceServers));
        sessionRef.current = session;

        await waitForSipAnswerAccepted(session, {
          mediaConstraints: AUDIO_MEDIA_CONSTRAINTS,
          mediaStream,
          pcConfig,
          rtcAnswerConstraints: {
            offerToReceiveAudio: true,
            offerToReceiveVideo: false
          }
        }, (reason, candidates) => {
          log(
            `🧊 Continuing SIP answer: ${reason} ` +
            `(host:${candidates.host}, srflx:${candidates.srflx}, relay:${candidates.relay})`
          );
        });

        setCurrentCall({ state: 'answered' });
        setIncomingCall(null);
        clearAutoAnswer();
        log(source === 'auto' ? '✅ Auto-answered incoming SIP call (SIP 200 OK)' : '✅ Call answered (SIP 200 OK)');
        return true;
      } catch (err) {
        clearAutoAnswer();
        log(`❌ ${source === 'auto' ? 'Auto-answer' : 'Answer'} error: ${err.message}`);
        return false;
      }
    })();

    answerInFlightRef.current = { session, promise: attempt };
    void attempt.finally(() => {
      if (answerInFlightRef.current.promise === attempt) {
        answerInFlightRef.current = { session: null, promise: null };
      }
    });

    return attempt;
  }, [clearAutoAnswer, ensureLocalAudioStream, log]);

  // Auto-connect when extension is loaded or changes
  useEffect(() => {
    // Listen for storage changes (when login stores extension)
    const handleStorageChange = () => {
      const extension = getExtension();
      
      if (extension && extension.isEnabled && !autoConnectAttempted.current && !registered) {
        autoConnectAttempted.current = true;
        log(`📞 Auto-connecting extension ${extension.extensionNumber} (${extension.displayName})`);
        
        connectRef.current?.(extension);
      }
    };

    // Check immediately on mount
    handleStorageChange();

    // Listen for storage events (from other tabs or manual changes)
    window.addEventListener('storage', handleStorageChange);
    
    // Also check periodically for first 5 seconds after mount (catches same-tab localStorage changes)
    const interval = setInterval(() => {
      if (!autoConnectAttempted.current) {
        handleStorageChange();
      }
    }, 1000);

    const stopPollingTimer = window.setTimeout(() => clearInterval(interval), 5000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
      window.clearTimeout(stopPollingTimer);
    };
  }, [getExtension, log, registered]);

  const connect = useCallback((extensionData) => {
    try {
      const extension = extensionData || getExtension();
      if (!extension) {
        log('❌ No extension data available');
        return;
      }

      if (registeredRef.current || uaRef.current) {
        log('SIP is already connecting or registered');
        return;
      }

      autoConnectAttempted.current = true;

      const server = extension.server || SIP_WS_URL;
      const user = extension.sipUsername || extension.extensionNumber;
      const pass = extension.sipPassword || extension.extensionNumber; // Fallback to extension number as password
      const displayName = extension.displayName || user;
      setStatus('Connecting');

      log(`🔌 Connecting to ${server} as ${user}...`);
      log(`🔑 Using password: ${pass ? '***' + pass.slice(-2) : 'NONE'}`);

      // Create JsSIP WebSocket with keepalive
      const socket = new JsSIP.WebSocketInterface(server);
      socket.via_transport = 'ws';
      
      // Enable WebSocket keepalive
      socket.onconnect = () => {
        log('🔗 WebSocket transport connected');
      };
      
      socket.ondisconnect = () => {
        log('⚠️ WebSocket transport disconnected');
      };
      
      const configuration = {
        sockets: [socket],
        uri: `sip:${user}@${SIP_DOMAIN}`,
        password: pass,
        display_name: displayName,
        register: true,
        register_expires: 300, // Re-register every 300 seconds (5 minutes)
        registrar_server: SIP_REGISTRAR,
        session_timers: false,
        use_preloaded_route: false,
        connection_recovery_min_interval: 2,
        connection_recovery_max_interval: 30
      };

      // Create User Agent
      const ua = new JsSIP.UA(configuration);

      // Connection events
      ua.on('connected', () => {
        log('✅ WebSocket connected');
        setStatus('Connected');
      });

      ua.on('disconnected', () => {
        log('❌ WebSocket disconnected');
        setStatus('Disconnected');
        setRegistered(false);
        clearAutoAnswer();
        if (!pageTeardownRef.current) {
          unregisterAgentPresence().catch(() => {});
        }
      });

      // Registration events
      ua.on('registering', () => {
        log('📝 Registering...');
        setStatus('Registering');
      });

      ua.on('registered', () => {
        log('✅ Registered successfully!');
        setStatus('Registered');
        setRegistered(true);
        syncAgentPresence().catch(() => {});
        
        // Clear any existing timers
        if (keepaliveIntervalRef.current) {
          clearInterval(keepaliveIntervalRef.current);
        }
        if (reregisterTimerRef.current) {
          clearTimeout(reregisterTimerRef.current);
        }
        
        // Start keepalive ping every 30 seconds
        keepaliveIntervalRef.current = setInterval(() => {
          if (uaRef.current && uaRef.current.isConnected() && uaRef.current.isRegistered()) {
            try {
              // JsSIP automatically sends keepalive on WebSocket transport
              // Just trigger re-registration if needed
              log('💓 Connection alive - registration active');
            } catch (e) {
              log('⚠️ Keepalive check failed: ' + e.message);
            }
          }
          // Send API heartbeat to bridge-api so agent stays registered
          sendAgentHeartbeat().catch(() => {});
        }, 30000);
        
        // Schedule re-registration at 80% of expiry time (240 seconds for 300s expiry)
        reregisterTimerRef.current = setTimeout(() => {
          if (uaRef.current && uaRef.current.isRegistered()) {
            log('🔄 Triggering re-registration...');
            uaRef.current.register();
          }
        }, 240000); // 240 seconds (4 minutes)
      });

      ua.on('unregistered', () => {
        log('⚠️ Unregistered');
        setStatus('Unregistered');
        setRegistered(false);
        clearAutoAnswer();
        if (!pageTeardownRef.current) {
          unregisterAgentPresence().catch(() => {});
        }
        
        // Clear timers on unregister
        if (keepaliveIntervalRef.current) {
          clearInterval(keepaliveIntervalRef.current);
          keepaliveIntervalRef.current = null;
        }
        if (reregisterTimerRef.current) {
          clearTimeout(reregisterTimerRef.current);
          reregisterTimerRef.current = null;
        }
      });

      ua.on('registrationFailed', (data) => {
        const reason = data.cause || data.response?.reason_phrase || 'Unknown';
        const statusCode = data.response?.status_code;
        log(`❌ Registration failed: ${reason} (Code: ${statusCode})`);
        log(`📋 Details: ${JSON.stringify(data.response || data)}`);
        setStatus(`Registration failed: ${reason}`);
        setRegistered(false);
      });

      // Incoming call
      ua.on('newRTCSession', (data) => {
        const session = data.session;

        if (session.direction === 'incoming') {
          const shouldAutoAnswer = autoAnswerRequestedRef.current.expiresAt > Date.now();
          if (shouldAutoAnswer) {
            log('📞 Prepared SIP agent leg received...');
          } else {
            log('📞 Incoming call...');
            setIncomingCall(session);
          }
          sessionRef.current = session;
          setMuted(false);

          // Attach remote audio
          session.on('peerconnection', (e) => {
            const peerconnection = e.peerconnection;
            wireRemoteAudio(peerconnection, 'incoming');
          });

          session.on('ended', () => {
            log('📴 Call ended');
            setCurrentCall(null);
            setIncomingCall(null);
            sessionRef.current = null;
            clearRemoteAudio();
            clearAutoAnswer();
          });

          session.on('failed', (e) => {
            log(`❌ Call failed: ${e.cause}`);
            setCurrentCall(null);
            setIncomingCall(null);
            sessionRef.current = null;
            clearRemoteAudio();
            clearAutoAnswer();
          });

          if (shouldAutoAnswer) {
            answerSession(session, 'auto').catch((error) => {
              log(`❌ Auto-answer failed: ${error?.message || error}`);
            });
          } else {
            clearAutoAnswer();
          }
        }
      });

      // Start the UA
      ua.start();
      uaRef.current = ua;

    } catch (err) {
      log('❌ Connect error: ' + (err?.message || String(err)));
      console.error('Full connect error:', err);
      setStatus('Error: ' + (err?.message || String(err)));
    }
  }, [answerSession, clearAutoAnswer, clearRemoteAudio, getExtension, log, wireRemoteAudio]);

  connectRef.current = connect;

  const prepareAutoAnswer = useCallback(async () => {
    const pendingIncomingSession = incomingCall || (
      sessionRef.current?.direction === 'incoming' ? sessionRef.current : null
    );
    if (pendingIncomingSession) {
      return answerSession(pendingIncomingSession, 'auto');
    }

    try {
      // Request microphone access while this function still runs from the
      // user's click. Waiting until the later SIP INVITE can leave JsSIP stuck
      // at 180 Ringing while the permission prompt is pending.
      await ensureLocalAudioStream();
      autoAnswerRequestedRef.current = { expiresAt: Date.now() + AUTO_ANSWER_WINDOW_MS };
      log(`🪄 Microphone ready; auto-answer armed for the next incoming SIP call (${Math.round(AUTO_ANSWER_WINDOW_MS / 1000)}s)`);
      return true;
    } catch (err) {
      clearAutoAnswer(true);
      log(`❌ Cannot prepare auto-answer: ${err.message}`);
      return false;
    }
  }, [answerSession, clearAutoAnswer, ensureLocalAudioStream, incomingCall, log]);

  const delay = useCallback((ms) => new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  }), []);

  const disconnect = useCallback(async () => {
    try {
      // Clear all timers
      if (keepaliveIntervalRef.current) {
        clearInterval(keepaliveIntervalRef.current);
        keepaliveIntervalRef.current = null;
      }
      if (reregisterTimerRef.current) {
        clearTimeout(reregisterTimerRef.current);
        reregisterTimerRef.current = null;
      }
      
      if (sessionRef.current) {
        sessionRef.current.terminate();
        sessionRef.current = null;
      }
      clearRemoteAudio();
      
      const ua = uaRef.current;
      uaRef.current = null;

      if (ua) {
        try {
          if (typeof ua.isRegistered === 'function' && ua.isRegistered() && typeof ua.unregister === 'function') {
            log('↩️ Sending SIP unregister...');
            ua.unregister({ all: true });
            await delay(400);
          }
        } catch (err) {
          log('⚠️ SIP unregister warning: ' + (err?.message || String(err)));
        }

        try {
          if (typeof ua.stop === 'function') {
            ua.stop();
          }
        } catch (err) {
          log('⚠️ SIP stop warning: ' + (err?.message || String(err)));
        }

        try {
          const transportSocket = ua.transport?.socket;
          if (transportSocket && typeof transportSocket.disconnect === 'function') {
            transportSocket.disconnect();
          } else if (transportSocket && typeof transportSocket.close === 'function') {
            transportSocket.close();
          }
        } catch (err) {
          log('⚠️ Socket close warning: ' + (err?.message || String(err)));
        }

        try {
          await delay(150);
        } catch {
          // Ignore teardown wait errors.
        }

        uaRef.current = null;
      }
      
      await unregisterAgentPresence().catch(() => false);
      setStatus('Disconnected');
      setRegistered(false);
      autoConnectAttempted.current = false;
      clearAutoAnswer();
      log('🔌 Disconnected');
    } catch (err) {
      log('❌ Disconnect error: ' + err.message);
    }
  }, [clearAutoAnswer, clearRemoteAudio, delay, log]);

  const makeCall = useCallback((destination) => {
    try {
      if (!uaRef.current || !registered) {
        log('❌ Not registered');
        return false;
      }

      const eventHandlers = {
        'peerconnection': (e) => {
          const peerconnection = e.peerconnection;
          wireRemoteAudio(peerconnection, 'outgoing');
        },
        'progress': (event) => {
          const statusCode = Number(event?.response?.status_code || event?.response?.statusCode || 0);
          if (statusCode === 180) {
            stopLocalRingback();
            log('SIP 180 Ringing received; fallback ringback cancelled');
          }
        },
        'accepted': () => {
          stopLocalRingback();
        },
        'confirmed': () => {
          // Call answered — stop the local ringback (remote audio now carries the call).
          stopLocalRingback();
        },
        'ended': () => {
          log('📴 Call ended');
          setCurrentCall(null);
          sessionRef.current = null;
          clearRemoteAudio();
        },
        'failed': (e) => {
          log(`❌ Call failed: ${e.cause}`);
          setCurrentCall(null);
          setIncomingCall(null);
          sessionRef.current = null;
          clearRemoteAudio();
        }
      };

      const pcConfig = buildPcConfig();
      log(describeIceServers(pcConfig.iceServers));

      const options = {
        eventHandlers,
        mediaConstraints: { audio: true, video: false },
        pcConfig
      };

      const session = uaRef.current.call(destination, options);
      sessionRef.current = session;
      setMuted(false);
      setCurrentCall({ destination, state: 'calling' });
      // Arm fallback ringback. SIP 180/early media/answer cancels it.
      startLocalRingback();
      log(`📞 Calling ${destination}`);
      return true;
    } catch (err) {
      log('❌ Call error: ' + err.message);
      // Clear call state on error
      setCurrentCall(null);
      sessionRef.current = null;
      stopLocalRingback();
      return false;
    }
  }, [clearRemoteAudio, log, registered, wireRemoteAudio, startLocalRingback, stopLocalRingback]);

  const answerCall = useCallback(() => {
    return answerSession(incomingCall, 'manual');
  }, [answerSession, incomingCall]);

  const hangup = useCallback(() => {
    try {
      const session = sessionRef.current;
      if (!session) {
        setMuted(false);
        setCurrentCall(null);
        setIncomingCall(null);
        log('📴 Hung up (no active session)');
        return false;
      }
      
      session.terminate();
      sessionRef.current = null;
      setCurrentCall(null);
      setIncomingCall(null);
      clearRemoteAudio();
      clearAutoAnswer();
      log('📴 Hung up');
      return true;
    } catch (err) {
      log('❌ Hangup error: ' + err.message);
      return false;
    }
  }, [clearAutoAnswer, clearRemoteAudio, log]);

  const toggleMute = useCallback(() => {
    try {
      const session = sessionRef.current;
      if (!session) return false;
      
      if (session.isMuted().audio) {
        session.unmute({ audio: true, video: false });
        setMuted(false);
        log('🔊 Unmuted');
      } else {
        session.mute({ audio: true, video: false });
        setMuted(true);
        log('🔇 Muted');
      }
      return true;
    } catch (err) {
      log('❌ Mute error: ' + err.message);
      return false;
    }
  }, [log]);

  const setHold = useCallback((shouldHold) => {
    try {
      const session = sessionRef.current;
      if (!session) {
        log('No active SIP call to hold/unhold');
        return false;
      }

      const method = shouldHold ? 'hold' : 'unhold';
      if (typeof session[method] !== 'function') {
        log(`SIP ${method} is not supported by the active session`);
        return false;
      }

      const started = session[method]({ useUpdate: true }, () => {
        log(shouldHold ? 'Call placed on SIP hold' : 'Call resumed from SIP hold');
      });
      return started !== false;
    } catch (err) {
      log(`SIP ${shouldHold ? 'hold' : 'unhold'} error: ${err.message}`);
      return false;
    }
  }, [log]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (!pageTeardownRef.current) {
        disconnect();
      }
    };
  }, [disconnect]);

  useEffect(() => {
    const handlePageTeardown = () => {
      pageTeardownRef.current = true;
    };
    const handlePageRestore = () => {
      pageTeardownRef.current = false;
    };

    window.addEventListener('pagehide', handlePageTeardown);
    window.addEventListener('beforeunload', handlePageTeardown);
    window.addEventListener('pageshow', handlePageRestore);

    return () => {
      window.removeEventListener('pagehide', handlePageTeardown);
      window.removeEventListener('beforeunload', handlePageTeardown);
      window.removeEventListener('pageshow', handlePageRestore);
    };
  }, []);

  const value = {
    status,
    registered,
    incomingCall,
    currentCall,
    muted,
    logs,
    connect,
    disconnect,
    makeCall,
    answerCall,
    prepareAutoAnswer,
    clearAutoAnswer,
    hangup,
    setHold,
    toggleMute,
    audioRef
  };

  return (
    <SoftphoneContext.Provider value={value}>
      {children}
      <audio ref={audioRef} autoPlay style={{ display: 'none' }} />
    </SoftphoneContext.Provider>
  );
};
